import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CirclePlus, Pencil } from "lucide-react";
import { DeleteBtn } from "@/components/Delete";
import { LoaderSpin } from "@/components/Loaders";
import { useToastNavigate } from "@/utils/toastNavigate";
import { VacationStatusConst, VacationStatusLabel } from "@/types/employeeFicha";
import {
  getEmployeeVacations,
  createEmployeeVacation,
  updateEmployeeVacation,
  deleteEmployeeVacation,
} from "@/Api/employeeVacations";

const schema = z.object({
  acquisition_period_start: z.string({ message: "*requerido" }).min(1, "*requerido"),
  acquisition_period_end: z.string({ message: "*requerido" }).min(1, "*requerido"),
  enjoyment_period_start: z.string().optional(),
  enjoyment_period_end: z.string().optional(),
  notes: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

const formatDate = (value?: string | null) =>
  value ? value.slice(0, 10).split("-").reverse().join("/") : "-";

const HistoryForm = ({
  employeeId,
  editing,
  onSaved,
}: {
  employeeId: number;
  editing?: any;
  onSaved: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState("default");
  const { toast } = useToastNavigate();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      acquisition_period_start: editing?.acquisition_period_start?.slice(0, 10) || "",
      acquisition_period_end: editing?.acquisition_period_end?.slice(0, 10) || "",
      enjoyment_period_start: editing?.enjoyment_period_start?.slice(0, 10) || "",
      enjoyment_period_end: editing?.enjoyment_period_end?.slice(0, 10) || "",
      notes: editing?.notes || "",
    },
  });

  const onSubmit = async (data: SchemaType) => {
    setState("loading");
    const payload = editing ? data : { ...data, employee_id: employeeId };

    const { data: apiResponse, ok } = editing
      ? await updateEmployeeVacation(editing.uuid, payload)
      : await createEmployeeVacation(payload);

    if (!ok) {
      setState("error");
      toast({
        description: apiResponse?.message || "Erro ao salvar",
        variant: "destructive",
      });
      setTimeout(() => setState("default"), 2000);
      return;
    }

    setState("default");
    setIsOpen(false);
    form.reset();
    toast({ description: "Salvo com sucesso!" });
    onSaved();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          >
            <Pencil className="size-5" />
          </Button>
        ) : (
          <Button type="button" size="sm" variant="outline" className="gap-1">
            <CirclePlus className="size-4" />
            Novas Férias
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogDescription className="text-lg">
            {editing ? "Editar Férias" : "Novo Registro de Férias"}
          </DialogDescription>
        </DialogHeader>
        <Form onSubmit={form.handleSubmit(onSubmit)} {...form} className="space-y-3">
          <label className="text-xs text-muted-foreground">Período Aquisitivo</label>
          <Form.Row>
            <Form.Field name="acquisition_period_start">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Início <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} type="date" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
            <Form.Field name="acquisition_period_end">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Fim <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} type="date" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <label className="text-xs text-muted-foreground">
            Período de Gozo (opcional)
          </label>
          <Form.Row>
            <Form.Field name="enjoyment_period_start">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Início</Form.Label>
                  <Form.Control>
                    <Input {...field} type="date" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
            <Form.Field name="enjoyment_period_end">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Fim</Form.Label>
                  <Form.Control>
                    <Input {...field} type="date" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="notes">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Observações</Form.Label>
                  <Form.Control>
                    <Textarea {...field} placeholder="Opcional" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>
          <DialogFooter>
            <Button type="submit" className="w-full" disabled={state !== "default"}>
              {state === "loading" ? <LoaderSpin /> : "Salvar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export const VacationSection = ({ employeeId }: { employeeId: number }) => {
  const [vacations, setVacations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    const { data } = await getEmployeeVacations(employeeId);
    setVacations(data?.response || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Férias</label>
        <HistoryForm employeeId={employeeId} onSaved={fetchData} />
      </div>
      {!loading && (
        <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
          <TableHeader>
            <TableRow>
              <TableHead>Período Aquisitivo</TableHead>
              <TableHead>Período de Gozo</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vacations.map((item) => (
              <TableRow key={item.uuid}>
                <TableCell>
                  {formatDate(item.acquisition_period_start)} a{" "}
                  {formatDate(item.acquisition_period_end)}
                </TableCell>
                <TableCell>
                  {item.enjoyment_period_start
                    ? `${formatDate(item.enjoyment_period_start)} a ${formatDate(item.enjoyment_period_end)}`
                    : "Não agendado"}
                </TableCell>
                <TableCell className="text-center">
                  <Badge
                    variant={
                      item.status === VacationStatusConst.TAKEN
                        ? "default"
                        : "outline"
                    }
                  >
                    {VacationStatusLabel[item.status]}
                  </Badge>
                </TableCell>
                <TableCell className="flex justify-center gap-1">
                  <HistoryForm employeeId={employeeId} editing={item} onSaved={fetchData} />
                  <DeleteBtn
                    params={{ func: deleteEmployeeVacation, funcParams: item.uuid }}
                    updateFront={fetchData}
                    referenceLabel={formatDate(item.acquisition_period_start)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {vacations.length < 1 && (
              <TableRow>
                <TableCell colSpan={4}>Nada foi encontrado.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
